import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserPlus, X, Mail, Lock, Phone, Play, Trophy, Calendar, Sun, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// High school teams that can have schedules
const HIGH_SCHOOL_TEAMS = ['2026', '2027', '2028', '2029', '2030'];
const YOUTH_TEAMS = ['10u', '11u', '12u', '13u', '14u'];
const ALL_TEAMS_WITH_SCHEDULES = [...YOUTH_TEAMS, ...HIGH_SCHOOL_TEAMS];

const TeamPage = () => {
  const { team } = useParams();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [teamVideo, setTeamVideo] = useState(null);
  const [standouts, setStandouts] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claimModal, setClaimModal] = useState({ open: false, player: null });
  const [claimForm, setClaimForm] = useState({ email: '', password: '', confirmPassword: '', phone: '' });
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [showSummerProgramPopup, setShowSummerProgramPopup] = useState(false);
  const [claimedAccountId, setClaimedAccountId] = useState(null);
  const [claimedToken, setClaimedToken] = useState(null);
  
  const teamTitle = team.toUpperCase();
  const hasScheduleSupport = ALL_TEAMS_WITH_SCHEDULES.includes(team);
  const isHighSchoolTeam = HIGH_SCHOOL_TEAMS.includes(team);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const requests = [
          axios.get(`${API}/players?team=${team}`),
          axios.get(`${API}/team-videos/${team}`),
          axios.get(`${API}/team-standouts/${team}`)
        ];
        
        // Fetch schedule for all teams
        if (hasScheduleSupport) {
          requests.push(axios.get(`${API}/team-schedules/${team}`));
        }
        
        const responses = await Promise.all(requests);
        setPlayers(responses[0].data);
        setTeamVideo(responses[1].data);
        setStandouts(responses[2].data || []);
        
        if (hasScheduleSupport && responses[3]) {
          setSchedule(responses[3].data);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching team data:', err);
        setError('Failed to load players. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [team, hasScheduleSupport]);

  const openClaimModal = (player) => {
    setClaimModal({ open: true, player });
    setClaimForm({ email: '', password: '', confirmPassword: '', phone: '' });
    setClaimError('');
    setClaimSuccess(false);
  };

  const closeClaimModal = () => {
    setClaimModal({ open: false, player: null });
    setClaimForm({ email: '', password: '', confirmPassword: '', phone: '' });
    setClaimError('');
    setClaimSuccess(false);
  };

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    setClaimError('');

    if (claimForm.password !== claimForm.confirmPassword) {
      setClaimError('Passwords do not match');
      return;
    }

    if (claimForm.password.length < 6) {
      setClaimError('Password must be at least 6 characters');
      return;
    }

    setClaimLoading(true);
    try {
      const response = await axios.post(`${API}/player/claim-profile`, {
        player_id: claimModal.player.id,
        email: claimForm.email,
        password: claimForm.password,
        phone: claimForm.phone || null
      });
      
      setClaimSuccess(true);
      setClaimedAccountId(response.data.account_id);
      setClaimedToken(response.data.token);
      
      // Update player in local state to show as claimed
      setPlayers(prev => prev.map(p => 
        p.id === claimModal.player.id 
          ? { ...p, claimed_by_account_id: 'claimed' }
          : p
      ));
      
      // Show Summer Program popup after a short delay (only for high school teams)
      setTimeout(() => {
        setClaimModal({ open: false, player: null });
        if (isHighSchoolTeam) {
          setShowSummerProgramPopup(true);
        }
      }, 2000);
    } catch (err) {
      setClaimError(err.response?.data?.detail || 'Error claiming player locker. Please try again.');
    } finally {
      setClaimLoading(false);
    }
  };

  const handleJoinSummerProgram = () => {
    // Open Stripe Payment Link directly
    window.open('https://buy.stripe.com/eVq14madY29X0en6J94ZG02', '_blank');
  };

  const handleSkipSummerProgram = () => {
    setShowSummerProgramPopup(false);
    // Navigate to player login page
    navigate('/player/login');
  };

  if (loading) {
    return (
      <div className="page-content">
        <section className="team-hero">
          <div className="container">
            <h1 className="display-large">{teamTitle} Team</h1>
            <p className="body-large">Loading players...</p>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <section className="team-hero">
          <div className="container">
            <h1 className="display-large">{teamTitle} Team</h1>
            <p className="body-large error-message">{error}</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-content">
      <section className="team-hero">
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h1 className="display-large">{teamTitle} Team</h1>
              <p className="body-large">Meet our talented roster</p>
            </div>
            {/* Schedule Button for All Teams */}
            {hasScheduleSupport && schedule && schedule.schedule_url && (
              <a
                href={schedule.schedule_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: 'var(--brand-primary)',
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '1rem',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(212, 175, 55, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.3)';
                }}
                data-testid="schedule-button"
              >
                <Calendar size={20} />
                SCHEDULE
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Team Video Section */}
      {teamVideo && teamVideo.video_url && (
        <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)', background: 'var(--bg-secondary)' }}>
          <div className="container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <Play size={28} style={{ color: 'var(--brand-primary)' }} />
              <h2 style={{ margin: 0 }}>{teamVideo.title || `${teamTitle} Team Video`}</h2>
            </div>
            <div style={{
              position: 'relative',
              paddingBottom: '56.25%',
              height: 0,
              overflow: 'hidden',
              borderRadius: '16px',
              background: '#000'
            }}>
              <video
                src={teamVideo.video_url}
                controls
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Tournament Standouts Section */}
      {standouts.length > 0 && (
        <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
          <div className="container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <Trophy size={28} style={{ color: 'var(--brand-primary)' }} />
              <h2 style={{ margin: 0 }}>Tournament Standouts</h2>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              {standouts.map((standout) => (
                <div key={standout.id} className="network-card" style={{ overflow: 'hidden' }}>
                  {standout.media_type === 'video' ? (
                    <video
                      src={standout.media_url}
                      controls
                      style={{ width: '100%', height: '200px', objectFit: 'cover', background: '#000' }}
                    />
                  ) : (
                    <img
                      src={standout.media_url}
                      alt={standout.title}
                      style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                    />
                  )}
                  <div style={{ padding: '16px' }}>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem' }}>{standout.title}</h3>
                    {standout.description && (
                      <p style={{ margin: 0, color: 'var(--text-light)', fontSize: '0.875rem' }}>
                        {standout.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="team-roster">
        <div className="container">
          {players.length === 0 ? (
            <p className="body-large" style={{ textAlign: 'center' }}>No players found for this team yet.</p>
          ) : (
            <div className="players-grid">
              {players.map((player) => (
                <div key={player.id} className="player-card network-card">
                  <div className="player-photo">
                    <img src={player.photo_url} alt={player.name} />
                  </div>
                  <div className="player-info">
                    <h3 className="network-card-title">{player.name}</h3>
                    <div className="player-details">
                      <div className="detail-row">
                        <span className="detail-label">Position:</span>
                        <span className="detail-value">{player.position}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Height:</span>
                        <span className="detail-value">{player.height}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Weight:</span>
                        <span className="detail-value">{player.weight}</span>
                      </div>
                      {player.school && (
                        <div className="detail-row">
                          <span className="detail-label">School:</span>
                          <span className="detail-value">{player.school}</span>
                        </div>
                      )}
                    </div>
                    <div className="player-stats">
                      <h4 className="stats-heading">Performance Stats</h4>
                      <div className="stats-grid">
                        <div className="stat-item">
                          <span className="stat-label">60YD</span>
                          <span className="stat-value">{player.sixty_yard_time}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">H-1ST</span>
                          <span className="stat-value">{player.home_to_first}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">VELO</span>
                          <span className="stat-value">{player.velo}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">EXIT V</span>
                          <span className="stat-value">{player.exit_velo}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">B-JUMP</span>
                          <span className="stat-value">{player.broad_jump}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">V-JUMP</span>
                          <span className="stat-value">{player.vertical_jump}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Claim Player Locker Button */}
                    {player.claimed_by_account_id ? (
                      <div style={{
                        marginTop: '16px',
                        padding: '8px 16px',
                        background: 'rgba(34, 197, 94, 0.2)',
                        border: '1px solid #22c55e',
                        borderRadius: '8px',
                        textAlign: 'center',
                        fontSize: '0.875rem',
                        color: '#22c55e'
                      }}>
                        Locker Claimed
                      </div>
                    ) : (
                      <button
                        onClick={() => openClaimModal(player)}
                        className="btn-secondary"
                        style={{
                          marginTop: '16px',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                        data-testid={`claim-locker-btn-${player.id}`}
                      >
                        <UserPlus size={16} />
                        Claim Player Locker
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Claim Player Locker Modal */}
      {claimModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '450px',
            width: '100%',
            position: 'relative'
          }}>
            <button
              onClick={closeClaimModal}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-light)'
              }}
            >
              <X size={24} />
            </button>

            {claimSuccess ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'rgba(34, 197, 94, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <UserPlus size={32} style={{ color: '#22c55e' }} />
                </div>
                <h2 style={{ marginBottom: '8px' }}>Player Locker Claimed!</h2>
                <p style={{ color: 'var(--text-light)' }}>
                  You can now log in to manage your profile.
                </p>
              </div>
            ) : (
              <>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <UserPlus size={40} style={{ color: 'var(--brand-primary)', marginBottom: '12px' }} />
                  <h2 style={{ marginBottom: '4px' }}>Claim Your Player Locker</h2>
                  <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
                    Create an account to manage <strong>{claimModal.player?.name}</strong>'s player locker
                  </p>
                </div>

                {claimError && (
                  <div style={{
                    padding: '12px 16px',
                    background: 'rgba(220, 38, 38, 0.2)',
                    border: '1px solid #dc2626',
                    borderRadius: '8px',
                    color: '#ef4444',
                    marginBottom: '16px',
                    fontSize: '0.875rem'
                  }}>
                    {claimError}
                  </div>
                )}

                <form onSubmit={handleClaimSubmit}>
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Mail size={16} /> Email
                    </label>
                    <input
                      type="email"
                      value={claimForm.email}
                      onChange={(e) => setClaimForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      placeholder="Enter your email"
                      data-testid="claim-email-input"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Phone size={16} /> Phone Number
                    </label>
                    <input
                      type="tel"
                      value={claimForm.phone}
                      onChange={(e) => setClaimForm(prev => ({ ...prev, phone: e.target.value }))}
                      required
                      placeholder="Enter phone number"
                      data-testid="claim-phone-input"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Lock size={16} /> Password
                    </label>
                    <input
                      type="password"
                      value={claimForm.password}
                      onChange={(e) => setClaimForm(prev => ({ ...prev, password: e.target.value }))}
                      required
                      placeholder="Create a password (min 6 characters)"
                      data-testid="claim-password-input"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Lock size={16} /> Confirm Password
                    </label>
                    <input
                      type="password"
                      value={claimForm.confirmPassword}
                      onChange={(e) => setClaimForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      placeholder="Confirm your password"
                      data-testid="claim-confirm-password-input"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={claimLoading}
                    style={{ width: '100%' }}
                    data-testid="claim-submit-btn"
                  >
                    {claimLoading ? 'Claiming...' : 'Claim Player Locker'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Summer Program Popup - Shows after claiming locker for high school teams */}
      {showSummerProgramPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            border: '2px solid #f59e0b'
          }}>
            <button
              onClick={handleSkipSummerProgram}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#999'
              }}
              data-testid="close-summer-popup"
            >
              <X size={24} />
            </button>

            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Sun size={56} style={{ color: '#f59e0b', marginBottom: '16px' }} />
              <h2 style={{ marginBottom: '8px', fontSize: '1.5rem', color: '#ffffff' }}>Congratulations on Claiming Your Locker!</h2>
              <p style={{ color: '#cccccc' }}>
                Take your game to the next level with the Pacific Ghost Summer Program
              </p>
            </div>

            <div style={{ 
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.1) 100%)',
              border: '1px solid rgba(245, 158, 11, 0.5)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#f59e0b', textAlign: 'center', marginBottom: '4px' }}>
                $1,500
              </div>
              <p style={{ textAlign: 'center', color: '#cccccc', margin: 0, fontSize: '0.875rem' }}>
                Complete Summer Program
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '16px', fontSize: '1rem', color: '#f59e0b', fontWeight: 600 }}>What's Included:</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { text: 'Sunday Games (Local, Professionally Coached)', value: '$300/mo $1200 Total - INCLUDED' },
                  { text: 'College Showcase Events (Private, Invite Only)', value: '' },
                  { text: 'Premium Locker Access (PAC Ghost Website)', value: '$39/mo $180 - INCLUDED' },
                  { text: 'Premium Tournament Schedule (WWBA, Fall Classic)', value: 'Tournament fees NOT INCLUDED' },
                  { text: 'Monthly Metric Tracking', value: '$150/mo $600 Total - INCLUDED' },
                  { text: 'Speed and Strength Program', value: '$150/mo $600 Total - INCLUDED' },
                  { text: 'Monthly Grad Year Zoom Call (College Coaches & Advisors)', value: 'INCLUDED' },
                  { text: 'Phase 1 College Prep Course', value: '$500 - INCLUDED' },
                  { text: 'PG Showcase (PG Scouts & Rankings)', value: '$400 - INCLUDED' }
                ].map((feature, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '0.85rem', paddingBottom: '6px', borderBottom: idx < 8 ? '1px solid #333' : 'none' }}>
                    <Check size={16} style={{ color: '#22c55e', flexShrink: 0, marginTop: '2px' }} />
                    <div>
                      <span style={{ color: '#e5e5e5' }}>{feature.text}</span>
                      {feature.value && <span style={{ color: '#f59e0b', fontSize: '0.8rem', marginLeft: '6px' }}>{feature.value}</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%)',
                border: '1px solid rgba(34, 197, 94, 0.4)',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <span style={{ color: '#22c55e', fontWeight: 700, fontSize: '1rem' }}>$3,480 VALUE</span>
                <span style={{ color: '#e5e5e5', margin: '0 8px' }}>–</span>
                <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: '1rem' }}>Complete Summer Program Only $1,500</span>
              </div>
            </div>

            <button 
              onClick={handleJoinSummerProgram}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'black',
                border: 'none',
                padding: '14px 24px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '1rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}
              data-testid="popup-join-summer-program"
            >
              <Sun size={18} /> Reserve Your Spot - $250
            </button>

            <button 
              onClick={handleSkipSummerProgram}
              style={{
                width: '100%',
                background: 'transparent',
                color: '#999999',
                border: '1px solid #444444',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
              data-testid="skip-summer-program"
            >
              Maybe Later - Go to My Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPage;